import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

@Injectable({
	providedIn: 'root',
})
export class PdfGeneratorService {
	constructor() {}

	// Remove colunas especificadas de um objeto ou array de objetos
	removeColumns(object: any, columnsToRemove: string[]): any {
		if (Array.isArray(object)) {
			return object.map((item) => this.removeColumns(item, columnsToRemove));
		} else if (typeof object === 'object' && object !== null) {
			if (Array.isArray(object.available_task_analysts)) {
				object.available_task_analysts = object.available_task_analysts.map((analyst) => ({
					email: analyst.email,
				}));
			}
			if (Array.isArray(object.already_approved_analysts)) {
				object.already_approved_analysts = object.already_approved_analysts.map((analyst) => ({
					email: analyst.email,
				}));
			}

			const result = { ...object };
			for (const column of columnsToRemove) {
				delete result[column];
			}

			for (const key of Object.keys(result)) {
				result[key] = this.removeColumns(result[key], columnsToRemove);
			}

			return result;
		}
		return object;
	}

	// Formata texto removendo tags HTML e substituindo quebras de linha
	formatText(string_: string): string[] {
		if (!string_) return [''];
		return string_
			.replaceAll(/<\/?[^>]+>/gi, '') // Remove tags HTML
			.replaceAll(/<br\s*\/?>/gi, '\n') // Substitui quebras de linha HTML por \n
			.split('\n'); // Divide o texto em um array por quebras de linha
	}

	// Formata um valor para texto, tratando diferentes tipos de dados
	formatValue(value: any): string {
		if (typeof value === 'string') {
			return this.formatText(value).join('\n');
		} else if (Array.isArray(value)) {
			return value.map((item, index) => `\n${this.formatValue(item)}`).join('\n\n');
		} else if (typeof value === 'object' && value !== null) {
			return Object.entries(value)
				.map(([key, value_]) => `${key}: ${this.formatValue(value_)}`)
				.join('\n');
		} else {
			return String(value);
		}
	}

	// Gera o PDF a partir dos dados fornecidos e remove colunas especificadas
	generatePDF(data: any, columnsToRemove: string[]) {
		const cleanedData = this.removeColumns(data, columnsToRemove);
		const document = new jsPDF();
		const pageWidth = document.internal.pageSize.getWidth();
		const rowHeight = 10;
		let currentPageHeight = document.internal.pageSize.getHeight() - 40;

		// Adiciona o título na primeira página
		// const id = data.id || 'Não disponível';
		// const customer = data.customer || 'Não disponível';
		// doc.setFontSize(16);
		// doc.setFont('helvetica', 'bold');
		// doc.text(`Chamado: ${id} - MySupport, Cliente: ${customer}`, 10, 10);
		// doc.setFontSize(12);
		// doc.setFont('helvetica', 'normal');

		const tableRows = [];
		const arrayTables = [];
		let isArrayTableAdded = false;

		// Adiciona linhas de dados à tabela principal
		const addRows = (object: any, prefix: string = '') => {
			for (const key in object) {
				if (object.hasOwnProperty(key)) {
					const value = object[key];
					let formattedValue = '';

					if (typeof value === 'string') {
						formattedValue = this.formatText(value).join('\n');
					} else if (Array.isArray(value)) {
						tableRows.push({ field: `${prefix}${key}`, value: value.length === 0 ? 'Sem informação' : 'Possui informações' });
						if (value.length > 0) {
							isArrayTableAdded = true;
							arrayTables.push({ title: `${prefix}${key}`, data: value });
						}
					} else if (typeof value === 'object' && value !== null) {
						addRows(value, `${prefix}${key}.`);
					} else {
						formattedValue = String(value);
					}

					if (formattedValue) {
						tableRows.push({ field: `${prefix}${key}`, value: formattedValue });
					}
				}
			}
		};

		addRows(cleanedData);

		// Ordena os campos da tabela principal em ordem alfabética
		tableRows.sort((a, b) => a.field.localeCompare(b.field));

		// Gera a tabela principal
		autoTable(document, {
			body: tableRows.map((row) => [row.field, row.value]),
			theme: 'striped',
			styles: {
				fontSize: 12,
				cellPadding: 2,
				overflow: 'ellipsize',
				textColor: [0, 0, 0],
				lineColor: [0, 0, 0],
				lineWidth: 0.1,
			},
			columnStyles: {
				0: {
					cellWidth: pageWidth * 0.25,
					fillColor: [0, 102, 161], // Azul para coluna da esquerda
					textColor: [255, 255, 255],
				},
				1: {
					cellWidth: pageWidth * 0.65,
					fillColor: [255, 255, 255],
					textColor: [0, 0, 0],
				},
			},
			margin: { top: 30 }, // Ajusta a margem superior para dar espaço ao título
		});

		// Se houver tabelas de arrays, adicione-as
		if (isArrayTableAdded) {
			for (const arrayTable of arrayTables) {
				const columns = new Set<string>();
				const rows = arrayTable.data.map((item) => {
					const row = [];
					for (const [key, value] of Object.entries(item)) {
						columns.add(key);
						row.push(this.formatValue(value));
					}
					return row;
				});

				const columnArray = [...columns].sort(); // Ordena as colunas em ordem alfabética
				const numberColumnsPerPage = 9;

				// Processa e adiciona as tabelas de arrays
				const processTable = (startCol: number, endCol: number) => {
					const currentColumns = columnArray.slice(startCol, endCol);
					const sortedRows = rows.map((row) => {
						const sortedRow = [];
						for (const col of currentColumns) {
							const index = Object.keys(arrayTable.data[0]).indexOf(col);
							sortedRow.push(index === -1 ? '' : row[index]);
						}
						return sortedRow;
					});

					const tableHeight = sortedRows.length * (rowHeight * 1.2) + 20; // Aumentar altura das linhas
					if (tableHeight > currentPageHeight) {
						document.addPage();
						currentPageHeight = document.internal.pageSize.getHeight() - 40;
					}

					autoTable(document, {
						head: [
							[
								{
									content: arrayTable.title,
									colSpan: currentColumns.length,
									styles: {
										fillColor: [0, 102, 161],
										textColor: [255, 255, 255],
										halign: 'center',
									},
								},
							],
							currentColumns,
						],
						body: sortedRows,
						theme: 'striped',
						styles: {
							fontSize: 8, // Aumentar tamanho da fonte
							cellPadding: 3, // Aumentar padding das células
							overflow: 'linebreak',
							textColor: [0, 0, 0],
							lineColor: [0, 0, 0],
							lineWidth: 0.1,
						},
						columnStyles: currentColumns.reduce((accumulator, col, index) => {
							if (col === 'predecessor' || col === 'task_order_number') {
								accumulator[index] = {
									cellWidth: (pageWidth / (currentColumns.length + 1)) * 0.75, // Diminuir o tamanho das colunas acima
									fillColor: [255, 255, 255], // Cor de fundo padrão
									textColor: [0, 0, 0],
								};
							} else {
								accumulator[index] = {
									cellWidth: (pageWidth / (currentColumns.length + 1)) * 1.05,
									fillColor: [255, 255, 255],
									textColor: [0, 0, 0],
								};
							}
							return accumulator;
						}, {}),
						margin: { top: 10, left: 10, bottom: 10, right: 5 },
					});

					currentPageHeight -= tableHeight;
				};

				// Adiciona uma nova página antes de processar a tabela de array
				document.addPage();
				currentPageHeight = document.internal.pageSize.getHeight() - 40;

				for (let startCol = 0; startCol < columnArray.length; startCol += numberColumnsPerPage) {
					const endCol = Math.min(startCol + numberColumnsPerPage, columnArray.length);
					processTable(startCol, endCol);
				}
			}
		}

		document.save('Report.pdf');
	}
}
